import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useGetAddresses, useDeleteAddress, useCreateAddress, useUpdateAddress } from '@/services/api/hooks/address';
import { AddressDto, AddressRequest } from '@/services/api/contracts/address';
import { Button } from '@/components/form/Button';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Modal } from '@/components/modal/Modal';
import { AddressAutocomplete, AutocompleteAddressData } from '@/components/form/fields/address/AddressAutocomplete';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextInput } from '@/components/form/fields/text/TextInput';
import { toast } from 'sonner';

export const SavedAddressesPage: React.FC = () => {
  const { partnerId, customerId } = useParams<{ partnerId: string; customerId: string }>();
  const { mspId, companyId } = useParams<{ mspId?: string; companyId?: string }>();
  
  const effectiveMspId = partnerId || mspId;
  const effectiveCustomerId = customerId || companyId;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressDto | null>(null);

  const [addresses, query] = useGetAddresses(effectiveMspId, effectiveCustomerId);
  const isLoading = query.isLoading;
  const deleteAddress = useDeleteAddress();
  const createAddress = useCreateAddress();
  const updateAddress = useUpdateAddress();

  const handleOpenEdit = (address: AddressDto) => {
    setSelectedAddress(address);
    setIsEditModalOpen(true);
  };

  const handleOpenDelete = (address: AddressDto) => {
    setSelectedAddress(address);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAddress || !effectiveMspId) return;
    try {
      await deleteAddress.mutateAsync({
        id: selectedAddress.id,
        mspId: effectiveMspId,
        customerId: effectiveCustomerId
      });
      toast.success('Address deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedAddress(null);
    } catch (error: any) {
      toast.error('Failed to delete address');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Saved Addresses</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your delivery and billing addresses</p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add New Address
        </Button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-lg shadow border border-slate-200 dark:border-slate-800 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500">Loading addresses...</div>
        ) : !addresses || addresses.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No saved addresses found. Click 'Add New Address' to create one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-700 uppercase bg-slate-50 dark:bg-slate-800 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-4 font-medium">Name</th>
                  <th className="px-6 py-4 font-medium">Address</th>
                  <th className="px-6 py-4 font-medium">City / State</th>
                  <th className="px-6 py-4 font-medium">Postcode</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {addresses.map((address: AddressDto) => (
                  <tr key={address.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">{address.name}</td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {address.line1}
                      {address.line2 && <><br /><span className="text-slate-500">{address.line2}</span></>}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {address.city}
                      {address.state && <>, {address.state}</>}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{address.postcode}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button type="button" onClick={() => handleOpenEdit(address)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button type="button" onClick={() => handleOpenDelete(address)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} open={() => {}} close={() => setIsDeleteModalOpen(false)} className="max-w-md bg-white p-6 rounded-lg">
        <Modal.Controls close={() => setIsDeleteModalOpen(false)} isOpen={isDeleteModalOpen} open={() => {}}>
          <h2 className="text-lg font-semibold">Delete Address</h2>
        </Modal.Controls>
        <div className="py-4 text-slate-600 dark:text-slate-300">
          Are you sure you want to delete the address <strong>{selectedAddress?.name}</strong>? This action cannot be undone.
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
          <Button type="button" onClick={handleDeleteConfirm}>Delete Address</Button>
        </div>
      </Modal>

      {/* Add Address Modal */}
      {effectiveMspId && (
        <AddAddressModal 
          isOpen={isAddModalOpen} 
          onClose={() => setIsAddModalOpen(false)} 
          mspId={effectiveMspId} 
          customerId={effectiveCustomerId} 
          onCreate={async (payload) => {
            await createAddress.mutateAsync({ ...payload, mspId: effectiveMspId, customerId: effectiveCustomerId });
          }}
        />
      )}

      {/* Edit Address Modal */}
      {isEditModalOpen && selectedAddress && effectiveMspId && (
        <EditAddressModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAddress(null);
          }}
          address={selectedAddress}
          mspId={effectiveMspId}
          customerId={effectiveCustomerId}
          onUpdate={async (payload) => {
            await updateAddress.mutateAsync({ ...payload, id: selectedAddress.id, mspId: effectiveMspId, customerId: effectiveCustomerId });
          }}
        />
      )}
    </div>
  );
};

// --- Add Address Modal using Loqate ---
const AddAddressModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  mspId: string;
  customerId?: string;
  onCreate: (payload: AddressRequest) => Promise<void>;
}> = ({ isOpen, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<AutocompleteAddressData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !selectedAddress) return;
    setIsSubmitting(true);
    try {
      await onCreate({
        name,
        line1: selectedAddress.line1,
        line2: selectedAddress.line2,
        city: selectedAddress.city,
        state: selectedAddress.state,
        country: selectedAddress.country,
        postcode: selectedAddress.postcode,
      });
      toast.success('Address created successfully');
      onClose();
      setName('');
      setSelectedAddress(null);
    } catch (error: any) {
      toast.error('Failed to create address');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} open={() => {}} close={onClose} className="max-w-[500px] bg-white p-6 rounded-lg overflow-visible">
      <Modal.Controls close={onClose} isOpen={isOpen} open={() => {}}>
        <h2 className="text-lg font-semibold">Add New Address</h2>
      </Modal.Controls>
      <div className="py-4 flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Location Name / Alias (e.g. Head Office)
          </label>
          <TextInput 
            id="name"
            value={name} 
            onChange={(val: string) => setName(val)} 
            placeholder="Enter a descriptive name" 
          />
        </div>
        <div className="relative z-50">
          <AddressAutocomplete 
            label="Search for Address" 
            onChange={(data: AutocompleteAddressData) => setSelectedAddress(data)}
          />
        </div>
        
        {selectedAddress && (
          <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 text-sm">
            <p className="font-semibold mb-2 text-slate-900 dark:text-slate-100">Selected Address:</p>
            <p>{selectedAddress.line1}</p>
            {selectedAddress.line2 && <p>{selectedAddress.line2}</p>}
            <p>{selectedAddress.city}{selectedAddress.state ? `, ${selectedAddress.state}` : ''}</p>
            <p>{selectedAddress.postcode}</p>
            {selectedAddress.country && <p>{selectedAddress.country}</p>}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button type="button" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button type="button" onClick={handleSubmit} disabled={!name.trim() || !selectedAddress || isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Address'}
        </Button>
      </div>
    </Modal>
  );
};

// --- Edit Address Modal using Standard RHF ---
const EditAddressModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  address: AddressDto;
  mspId: string;
  customerId?: string;
  onUpdate: (payload: AddressRequest) => Promise<void>;
}> = ({ isOpen, onClose, address, onUpdate }) => {
  const { control, handleSubmit, formState: { isSubmitting, errors } } = useForm<AddressRequest>({
    resolver: zodResolver(AddressRequest),
    defaultValues: {
      name: address.name,
      line1: address.line1,
      line2: address.line2 || undefined,
      city: address.city,
      state: address.state || undefined,
      country: address.country || undefined,
      postcode: address.postcode
    }
  });

  const onSubmit = async (data: AddressRequest) => {
    try {
      await onUpdate(data);
      toast.success('Address updated successfully');
      onClose();
    } catch (error: any) {
      toast.error('Failed to update address');
    }
  };

  return (
    <Modal isOpen={isOpen} open={() => {}} close={onClose} className="max-w-[500px] bg-white p-6 rounded-lg">
      <Modal.Controls close={onClose} isOpen={isOpen} open={() => {}}>
        <h2 className="text-lg font-semibold">Edit Address</h2>
      </Modal.Controls>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 py-4">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Location Name</label>
              <TextInput id="edit-name" value={field.value} onChange={field.onChange} />
              {errors.name && <span className="text-red-500 text-xs mt-1">{errors.name.message}</span>}
            </div>
          )}
        />
        <Controller
          name="line1"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Line 1</label>
              <TextInput id="edit-line1" value={field.value} onChange={field.onChange} />
              {errors.line1 && <span className="text-red-500 text-xs mt-1">{errors.line1.message}</span>}
            </div>
          )}
        />
        <Controller
          name="line2"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Line 2</label>
              <TextInput id="edit-line2" value={field.value || ''} onChange={field.onChange} />
            </div>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <TextInput id="edit-city" value={field.value} onChange={field.onChange} />
                {errors.city && <span className="text-red-500 text-xs mt-1">{errors.city.message}</span>}
              </div>
            )}
          />
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">State / Province</label>
                <TextInput id="edit-state" value={field.value || ''} onChange={field.onChange} />
              </div>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="postcode"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Postcode</label>
                <TextInput id="edit-postcode" value={field.value} onChange={field.onChange} />
                {errors.postcode && <span className="text-red-500 text-xs mt-1">{errors.postcode.message}</span>}
              </div>
            )}
          />
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <TextInput id="edit-country" value={field.value || ''} onChange={field.onChange} />
              </div>
            )}
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
